import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { CalendarCheck, HelpCircle, ShieldCheck } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useIsMobile } from "@/hooks/use-mobile";
import arshaImage from "@/assets/arshajosefsson.jpeg";
import { getUpdatedDate } from "@/utils/dateUtils";

interface ReviewedByProps {
  variant?: "default" | "hero";
  containerClassName?: string;
}

export const ReviewedBy = ({ variant = "default", containerClassName = "max-w-6xl" }: ReviewedByProps) => {
  const { currentLanguage } = useLanguage();
  const isMobile = useIsMobile();

  const ExpertBadge = () => (
    isMobile ? (
      <Popover>
        <PopoverTrigger asChild>
          <button className="inline-flex">
            <HelpCircle className="w-3.5 h-3.5 text-muted-foreground cursor-pointer" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-2">
          <p className="text-xs">Ämnesexpert</p>
        </PopoverContent>
      </Popover>
    ) : (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <HelpCircle className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs">Ämnesexpert</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  );

  if (variant === "hero") {
    return (
      <div className="inline-flex items-center gap-3 px-4 py-2.5 rounded-lg bg-white/95 backdrop-blur-sm border border-gray-200 shadow-sm">
        <img 
          src={arshaImage} 
          alt="Arsha Josefsson" 
          className="w-12 h-12 rounded-full object-cover border-2 border-gray-300 flex-shrink-0"
        />
        <div className="flex flex-col justify-center">
          <p className="text-xs text-gray-600 leading-tight">Granskad av:</p>
          <div className="flex items-center gap-1.5">
            <Link 
              to={`/${currentLanguage}/om-arsha-josefsson`}
              className="font-semibold text-sm text-gray-900 hover:text-primary hover:underline transition-colors leading-tight"
            >
              Arsha Josefsson
            </Link>
            <ExpertBadge />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6 md:py-8 bg-muted/20">
      <div className={`container ${containerClassName}`}>
        <div className="flex flex-col gap-4 rounded-lg border bg-background p-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
          <img 
            src={arshaImage} 
            alt="Arsha Josefsson" 
            className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover border border-border flex-shrink-0"
          />
          <div className="flex flex-col justify-center">
            <p className="text-xs text-muted-foreground leading-tight">Granskad av:</p>
            <div className="flex items-center gap-1.5">
              <Link 
                to={`/${currentLanguage}/om-arsha-josefsson`}
                className="font-semibold text-sm text-foreground hover:text-primary hover:underline transition-colors leading-tight"
              >
                Arsha Josefsson
              </Link>
              <ExpertBadge />
            </div>
          </div>
          </div>
          <div className="grid gap-2 text-sm text-muted-foreground sm:grid-cols-2 md:min-w-[420px]">
            <div className="flex items-center gap-2">
              <CalendarCheck className="h-4 w-4 text-primary" />
              <span>Senast faktakontrollerad {getUpdatedDate()}</span>
            </div>
            <Link
              to={`/${currentLanguage}/redaktionen`}
              className="flex items-center gap-2 font-medium text-primary hover:underline"
            >
              <ShieldCheck className="h-4 w-4" />
              Så granskar vi innehåll
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
